import { snakeCase } from 'lodash-es'

import { apiUrl, isAbsolute } from 'src/api/api_base.js'
import { StatusCodeError } from 'src/services/errors/errors'

export const paramsString = (params = {}) => {
  if (params == null || params === undefined) return ''

  if (typeof params !== 'object' || Array.isArray(params)) {
    throw new TypeError('Params are not an object!')
  }

  const entries = (() => {
    if (params instanceof Map) {
      return [...params.entries()]
    } else {
      return Object.entries(params)
    }
  })()

  const arrays = []
  const nonArrays = []

  entries.forEach(([k, v]) => {
    if (v == null) return // Drop nulls
    if (
      (typeof v === 'object' && !Array.isArray(v)) ||
      typeof v === 'function'
    ) {
      throw new TypeError('Param cannot be non-primitive!')
    }
    if (Array.isArray(v)) {
      arrays.push([k, v])
    } else {
      nonArrays.push([k, v])
    }
  })

  arrays.forEach(([k, array]) => {
    array.forEach((v) => {
      if (typeof v === 'object' || typeof v === 'function' || v === undefined)
        throw new TypeError('Array param cannot contain non-primitives!')
    })
  })

  if (nonArrays.length + arrays.length === 0) return ''

  return (
    '?' +
    [
      ...nonArrays.map(([k, v]) => [snakeCase(k), v]),
      // turning [a,[1,2,3]] into [[a[],1],[a[],2],[a[],3]]
      ...arrays.reduce(
        (acc, [k, arrayValue]) => [
          ...acc,
          ...arrayValue.map((v) => [snakeCase(k) + '[]', v]),
        ],
        [],
      ),
    ]
      .map(([k, v]) => `${k}=${window.encodeURIComponent(v)}`)
      .join('&')
  )
}

// The logged-in user's token, sent when a request names none. Set once at
// boot (the OAuth store); a store import here would be a cycle.
let tokenSource = () => undefined
export const setTokenSource = (source) => {
  tokenSource = source
}

// credentials: a token, null for none, or left out for the user's (only
// to the instance: never to another server)
const tokenFor = (url, credentials) =>
  credentials !== undefined
    ? credentials
    : isAbsolute(url)
      ? undefined
      : tokenSource()

export const promisedRequest = async ({
  method = 'GET',
  url,
  payload,
  formData,
  forceContentType,
  cache,
  credentials,
  headers = {},
}) => {
  const options = {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: forceContentType ?? 'application/json',
      ...headers,
    },
  }

  if (!formData) {
    options.headers['Content-Type'] = 'application/json'
  }

  if (cache) {
    options.cache = cache
  }

  if (formData || payload) {
    options.body = formData || JSON.stringify(payload)
  }

  const token = tokenFor(url, credentials)
  if (token) {
    options.headers = {
      ...options.headers,
      ...authHeaders(token),
    }
  }

  const response = await fetch(apiUrl(url), options)
  const data = await (async () => {
    // a reply may come without a type (204 No Content, some errors)
    const [contentType] = (response.headers.get('content-type') ?? '')
      .split(';')
      .map((x) => x.toLowerCase().trim())
    const contentLength = Number.parseInt(
      response.headers.get('content-length'),
    )
    if (contentLength === 0 || response.status === 204) return null
    if (!contentType && !forceContentType) {
      const text = await response.text()
      return text === '' ? null : text
    }

    switch (forceContentType ?? contentType) {
      case 'text/plain':
        return await response.text()
      case 'application/json':
        return await response.json()
      default:
        // an error page (HTML from a proxy) is read as text
        if (!response.ok) return await response.text()
        // not Response#bytes: Chrome 132 and Safari 18 only
        return new Uint8Array(await response.arrayBuffer())
    }
  })()

  const { ok, status } = response

  if (ok) {
    return { response, status, data, timestamp: Date.now() }
  } else {
    throw new StatusCodeError(response.status, data, { url, options }, response)
  }
}

const authHeaders = (accessToken) => {
  if (accessToken) {
    return { Authorization: `Bearer ${accessToken}` }
  } else {
    return {}
  }
}
